import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../../i18n/LanguageContext";

const BRANDS = ["FCA / Stellantis", "CA Auto Bank", "Drivalia", "Leasys"];

const Experience = () => {
  const { t } = useLang();
  // duplicate for seamless marquee
  const marqueeItems = [...BRANDS, ...BRANDS, ...BRANDS];

  return (
    <section
      id="experience"
      className="relative py-24 lg:py-32 text-[var(--ivory)] overflow-hidden"
      style={{ backgroundColor: "var(--navy)" }}
      data-testid="experience-section"
    >
      {/* Abstract premium navy background — pure CSS, no grid, no text */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 78% 18%, rgba(197,160,89,0.18) 0%, transparent 60%), radial-gradient(ellipse 70% 50% at 12% 82%, rgba(110,140,200,0.14) 0%, transparent 65%), radial-gradient(ellipse 60% 40% at 50% 50%, rgba(253,252,240,0.04) 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.05), transparent 40%), radial-gradient(circle at 85% 75%, rgba(197,160,89,0.10), transparent 45%)",
        }}
      />
      {/* Subtle film grain */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <span className="overline" style={{ color: "var(--champagne)" }} data-testid="experience-overline">
            {t.experience.overline}
          </span>
          <h2 className="font-serif text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.08] tracking-tight mt-6 text-[var(--ivory)]" data-testid="experience-title">
            {t.experience.title}
          </h2>
          <div className="mt-8 w-14 h-px bg-[var(--champagne)]" />
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-2 gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-[16px] lg:text-[17px] leading-[1.85] text-[#D7DEE8]"
            data-testid="experience-p1"
          >
            {t.experience.p1}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[16px] lg:text-[17px] leading-[1.85] text-[#D7DEE8]"
            data-testid="experience-p2"
          >
            {t.experience.p2}
          </motion.p>
        </div>

        {/* Brand marquee */}
        <div className="mt-16 border-y border-[var(--border-dark)] py-8 overflow-hidden" data-testid="experience-brands">
          <div className="marquee gap-16">
            {marqueeItems.map((b, i) => (
              <span
                key={i}
                className="font-serif text-[22px] lg:text-[26px] text-[#D7DEE8]/80 whitespace-nowrap px-8 italic"
              >
                {b}
              </span>
            ))}
          </div>
        </div>

        {/* Qualitative blocks */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-dark)]">
          {t.experience.blocks.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.07 }}
              className="bg-[var(--navy)] p-8 lg:p-10"
              data-testid={`experience-block-${i}`}
            >
              <p className="overline" style={{ color: "var(--champagne)" }}>
                {String(i + 1).padStart(2, "0")}
              </p>
              <p className="font-serif text-[22px] lg:text-[26px] text-[var(--ivory)] mt-3 leading-tight">
                {b}
              </p>
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-[12px] leading-[1.7] text-[#A0B0C4] max-w-3xl" data-testid="experience-disclaimer">
          {t.experience.disclaimer}
        </p>
      </div>
    </section>
  );
};

export default Experience;
